import { Typography } from '@mui/material'
import { FormHelperText, Grid } from '@vapor/material'
import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlgebraicSign, PaymentActivesQuery, PaymentTypeCode } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import PaymentRow from './payment-row'
import { PaymentRowAmount, PaymentSplitterError, Payment, PaymentRowProps } from './payment-splitter.types'
import { FiscalType } from 'src/api/api.generated'
import { v4 as uuidv4 } from 'uuid'

export type PaymentSplitterProps = {
  onChange: (value: PaymentRowAmount[]) => void
  readonly?: boolean
  disabled?: boolean
  error?: PaymentSplitterError
  dataForGatewayPayment?: (amount?: number | null, paymentActivesWithGateway?: PaymentActivesQuery, blockAmountChanges?: boolean) => void
  amount: number
  value: PaymentRowAmount[]
  fiscalType: FiscalType
  paymentActives: PaymentActivesQuery | undefined
  depositCreation?: true
  editMode?: 'FULL_EDIT' | 'PAYMENT_MODE_ONLY'
}

export default function PaymentSplitter(props: PaymentSplitterProps) {
  const { paymentActives, onChange, readonly, disabled, error, dataForGatewayPayment, depositCreation, fiscalType, editMode = 'FULL_EDIT', ...amountValue } = props

  const { t } = useTranslation()
  const [localAmount, setLocalAmount] = useState<number>(0)
  const [localValue, setLocalValue] = useState<PaymentRowProps[]>([])
  const localizeString = useLocalizeString()
  const disablePriceEdit = editMode === 'PAYMENT_MODE_ONLY'

  const onLocalChange = useCallback(
    (newRows: PaymentRowProps[]) => {
      //* combine all the rows with the same paymentTypeId
      const combinedNewRows = newRows.reduce((prev: PaymentRowProps[], curr: PaymentRowProps) => {
        const alreadyExistsIndex = prev.findIndex((r) => r.paymentActiveId === curr.paymentActiveId)
        if (alreadyExistsIndex >= 0) {
          const tmp = [...prev]
          tmp[alreadyExistsIndex] = {
            ...prev[alreadyExistsIndex],
            amount: (prev[alreadyExistsIndex]?.amount ?? 0) + (curr?.amount ?? 0),
          }
          return tmp
        } else {
          return [...prev, curr]
        }
      }, [])

      if (JSON.stringify(combinedNewRows) !== JSON.stringify(localValue)) {
        setLocalValue(combinedNewRows)
      }
      const newValue = toPaymentAmounts(combinedNewRows)

      if (JSON.stringify(newValue) !== JSON.stringify(amountValue.value)) {
        onChange(newValue)
      }
    },
    [localValue, amountValue.value],
  )

  useEffect(() => {
    const oldAmount = localAmount
    const newAmount = amountValue.amount
    const oldValueJson = JSON.stringify(toPaymentAmounts(localValue))
    const newValueJson = JSON.stringify(amountValue.value)

    if (newAmount === oldAmount && newValueJson === oldValueJson) {
      return
    }
    if (newValueJson !== oldValueJson) {
      setLocalAmount(Math.max(amountValue.amount, 0))
      onLocalChange(amountValue.value)
      return
    }

    if (amountValue.amount <= 0) {
      setLocalAmount(Math.max(amountValue.amount, 0))
      onLocalChange([])
      return
    }

    if (amountValue.amount === localValue.reduce((p, c) => p + (c.amount || 0), 0)) {
      setLocalAmount(amountValue.amount)
      return
    }

    setLocalAmount(amountValue.amount)

    const currentPaymentsAmount = localValue.reduce((prev, curr) => prev + (curr?.amount ?? 0), 0)
    let amountDiff = amountValue.amount - currentPaymentsAmount // Can be negative

    if (currentPaymentsAmount > amountValue.amount) {
      const updatedRows = localValue
        .map((r) => {
          const rowAmount = r?.amount ?? 0
          if (r.editable && amountDiff !== 0) {
            const removableQuantity = amountDiff + rowAmount
            const tmp = {
              ...r,
              amount: removableQuantity <= 0 ? 0 : removableQuantity,
            }

            amountDiff = Math.abs(amountDiff) <= rowAmount ? 0 : removableQuantity
            return tmp
          }
          return r
        })
        .filter((r) => r?.amount !== 0)
      onLocalChange(updatedRows)
    } else if (currentPaymentsAmount < amountValue.amount) {
      if (!localValue.find((r) => !r.paymentActiveId)) {
        onLocalChange([...localValue, { uuid: uuidv4(), paymentActiveId: null, amount: amountValue.amount - currentPaymentsAmount, editable: true }])
      } else {
        const updatedRows = localValue.map((r) => {
          const rowAmount = r?.amount ?? 0
          if (!r.paymentActiveId) {
            return {
              ...r,
              amount: rowAmount + amountDiff,
            }
          }
          return r
        })
        onLocalChange(updatedRows)
      }
    }
  }, [amountValue, localValue])

  const getSplitPayment = useCallback(
    (paymentActiveId: string | null) => {
      return paymentActives?.paymentActives?.find((pa) => {
        const paymentIsSplitPayment = pa.paymentType.code === PaymentTypeCode.SPLITPAYMENT
        return paymentIsSplitPayment && pa.id === paymentActiveId
      })
    },
    [paymentActives],
  )

  const isSplitPayment = useCallback(
    (paymentActiveId: string | null) => {
      return getSplitPayment(paymentActiveId) !== undefined
    },
    [paymentActives],
  )

  // Tutti i payment active
  const payments: PaymentActivesQuery['paymentActives'] = useMemo(() => {
    return (
      paymentActives?.paymentActives
        ?.filter((pa) => {
          const paymentIsPositiveDeposit = pa.paymentType.code === PaymentTypeCode.DEPOSIT && pa.paymentType.sign === AlgebraicSign.POSITIVE
          return !paymentIsPositiveDeposit
        })
        ?.filter((pa) => {
          const paymentIsSplitPayment = pa.paymentType.code === PaymentTypeCode.SPLITPAYMENT
          return !paymentIsSplitPayment
        })
        .filter((pa) => {
          const isPayedAndIsPositive = pa.paymentType.code !== PaymentTypeCode.UNPAID && pa.paymentType.sign === AlgebraicSign.POSITIVE
          const unpaidOnlyIfInvoice = pa.paymentType.code === PaymentTypeCode.UNPAID && fiscalType === FiscalType.INVOICE && pa.paymentType.sign === AlgebraicSign.POSITIVE
          const documentIsReadOnly = readonly
          return isPayedAndIsPositive || unpaidOnlyIfInvoice || documentIsReadOnly
        }) ||
      // .map((p) => {
      //   return {
      //     paymentActiveId: p.id,
      //     description: localizeString(p.name),
      //   }
      // })
      []
    )
  }, [paymentActives, fiscalType, readonly, localizeString])

  // Payment active collegati ad un gateway
  const paymentsWithGateway: Payment[] = useMemo(() => {
    return (
      paymentActives?.paymentActives
        ?.filter((pa) => {
          const paymentActiveWithGateway = pa.paymentMethods?.findIndex((pm) => pm.gateway?.type)
          return paymentActiveWithGateway !== undefined && paymentActiveWithGateway >= 0
        })
        ?.map((p) => {
          return {
            paymentActiveId: p.id,
            description: localizeString(p.name),
          }
        }) || []
    )
  }, [paymentActives, localizeString])

  // Payment active di tipo sospeso
  const unpaidPaymentsMethods: Payment[] = useMemo(() => {
    return (
      paymentActives?.paymentActives
        ?.filter((pa) => pa.paymentType.code === PaymentTypeCode.UNPAID)
        ?.map<Payment>((p) => {
          return {
            paymentActiveId: p.id,
            description: localizeString(p.name),
          }
        }) || []
    )
  }, [paymentActives, localizeString])

  // Restituisce i payment active non utilizzati ed esclude i metodi di pagamento con gateway
  const getRowPayments = useCallback(
    (index: number, onlyOfSameType: boolean) => {
      const currentIndexPaymentActiveId = localValue.find((r, i) => i === index)?.paymentActiveId
      const currentIndexPaymentType = currentIndexPaymentActiveId ? payments.find((p) => p.id === currentIndexPaymentActiveId)?.paymentType : undefined

      const otherRowsPayments: string[] = localValue
        .filter((r, i) => i !== index)
        .reduce((previousValue: string[], currentValue) => {
          if (currentValue.paymentActiveId) {
            return [...previousValue, currentValue.paymentActiveId]
          } else {
            return previousValue
          }
        }, [])
      let paymentsOptions = payments.filter((p) => !paymentsWithGateway.map((pa) => pa.paymentActiveId).includes(p.id)).filter((p) => !otherRowsPayments.includes(p.id))

      if (onlyOfSameType) {
        if (currentIndexPaymentType) {
          paymentsOptions = paymentsOptions.filter((p) => p.paymentType.code === currentIndexPaymentType.code && p.paymentType.sign === currentIndexPaymentType.sign)
        } else {
          paymentsOptions = []
        }
      }

      if (!localValue[index].paymentActiveId || paymentsOptions.findIndex((x) => x.id === localValue[index].paymentActiveId) >= 0) {
        return paymentsOptions.map<Payment>((p) => ({ paymentActiveId: p.id, description: localizeString(p.name) }))
      }

      return paymentsWithGateway
    },
    [localValue, payments, localizeString],
  )

  const onRowChange = useCallback(
    (index: number, amount: number | null | undefined, paymentActiveId: string | null | undefined, expireDate?: Date | null) => {
      const oldRow: PaymentRowProps | undefined = localValue[index]

      const newRow: PaymentRowProps =
        oldRow !== undefined
          ? {
              uuid: oldRow.uuid,
              amount: amount !== null && amount !== undefined ? amount : oldRow.amount,
              paymentActiveId: paymentActiveId ? paymentActiveId : oldRow.paymentActiveId,
              startOpen: false,
              editable: oldRow.editable,
              expireDate: expireDate,
            }
          : {
              uuid: uuidv4(),
              amount: amount !== null && amount !== undefined ? amount : null,
              paymentActiveId: paymentActiveId ? paymentActiveId : null,
              startOpen: true,
              editable: true,
              expireDate: expireDate,
            }

      // console.log('NEW ROW')
      // console.log(newRow)
      // console.log('=========================')

      const newRows = [...localValue]
      newRows[index] = newRow

      onLocalChange(newRows)
    },
    [localValue, onLocalChange],
  )

  //* Trigger the creation of the row with the null activePaymentId
  const onAmountBlur = useCallback(() => {
    const sum = localValue.reduce((s, r) => {
      if (r.paymentActiveId) {
        return s + (r.amount ? r.amount : 0)
      }
      return s
    }, 0)

    const remainToPay = localValue.find((p) => !p.paymentActiveId)?.amount ?? 0

    if (sum < localAmount) {
      onRowChange(localValue.length, localAmount - (sum + remainToPay), null)
    } else if (sum >= localAmount) {
      onLocalChange([...localValue.filter((value) => value.paymentActiveId)])
    }
  }, [localValue, onRowChange, localAmount])

  const onDeleteRow = useCallback(
    (index: number) => {
      if (index <= 0) return
      const row = localValue[index]
      if (row) {
        const newRows = localValue.filter((value, i) => i !== index)

        // Cerca la prima riga il cui metodo di pagamento non è associato ad un gateway o che non sia una riga editabile
        let targetRow = 0
        while (targetRow <= newRows.length - 1 && (isPaymentWithGateway(newRows[targetRow].paymentActiveId) || !newRows[targetRow].editable)) targetRow++

        if (targetRow > newRows.length - 1)
          newRows.push({
            uuid: row.uuid,
            amount: row.amount,
            paymentActiveId: null,
            editable: true,
          })
        else
          newRows[targetRow] = {
            uuid: newRows[targetRow].uuid,
            amount: (newRows[targetRow].amount || 0) + (row.amount || 0),
            paymentActiveId: newRows[targetRow].paymentActiveId,
            editable: newRows[targetRow].editable,
          }

        onLocalChange(newRows)
      }
    },
    [localValue],
  )

  const errorMessage = useMemo(() => (error && 'message' in error ? error.message : undefined), [error])

  const getRowError = useCallback(
    (index: number) => {
      if (!Array.isArray(error)) {
        return undefined
      }
      return error[index]
    },
    [error],
  )

  useEffect(() => {
    if (dataForGatewayPayment) {
      const paymentActivesWithGateway = paymentActives?.paymentActives.filter((pa) => pa.paymentMethods?.some((pm) => pm.gateway !== undefined))
      const paymentActivesWithGatewayArg = paymentActivesWithGateway ? ({ paymentActives: paymentActivesWithGateway } as PaymentActivesQuery) : undefined

      const amount = localValue?.reduce((prev, next) => {
        if (!next.paymentActiveId) return prev + (next.amount || 0)
        else return prev
      }, 0)

      // Se c'è almeno un pagamento effettuato tramite gateway, blocca la possibilità di effettuare modifiche all'importo poichè potrebbero venirsi a creare casi di difficile risoluzione.
      const blockAmountChanges = localValue.some((x) => isPaymentWithGateway(x.paymentActiveId))

      dataForGatewayPayment(amount, paymentActivesWithGatewayArg, blockAmountChanges)
    }
  }, [localValue, paymentActives])

  const isPaymentWithGateway = useCallback(
    (paymentActiveId: string | null) => {
      return paymentsWithGateway.findIndex((x) => x.paymentActiveId === paymentActiveId) >= 0
    },
    [paymentsWithGateway],
  )

  const isUnpaidPaymentMethod = useCallback(
    (paymentActiveId: string | null) => {
      return unpaidPaymentsMethods.findIndex((x) => x.paymentActiveId === paymentActiveId) >= 0
    },
    [unpaidPaymentsMethods],
  )

  return (
    <Grid container spacing={1}>
      {errorMessage && (
        <Grid item xs={12} style={{ paddingTop: 0 }}>
          <FormHelperText style={{ paddingTop: 0, marginTop: 0 }} error>
            {errorMessage}
          </FormHelperText>
        </Grid>
      )}

      {localAmount <= 0 && localValue.length <= 0 && (
        <Grid item xs={12}>
          <Typography>{t('pms.common.payment_splitter.no_payments')}</Typography>
        </Grid>
      )}
      {localValue.map((row, index) => {
        const splitPayment = getSplitPayment(row.paymentActiveId)
        const rowPayments = splitPayment
          ? [
              {
                paymentActiveId: splitPayment.id,
                description: localizeString(splitPayment.name),
              },
            ]
          : getRowPayments(index, editMode === 'PAYMENT_MODE_ONLY')
        const numberOfNonEditableRows = localValue.filter((v) => !v.editable).length

        return (
          <React.Fragment key={index}>
            <PaymentRow
              numberOfNonEditableRows={numberOfNonEditableRows}
              index={index}
              payments={rowPayments}
              amount={row?.amount || null}
              expireDate={row?.expireDate || null}
              onAmountChange={(value) => {
                // console.log('>> onAmountChange')
                onRowChange(index, value, undefined, row.expireDate)
              }}
              onAmountBlur={onAmountBlur}
              paymentActiveId={row.paymentActiveId}
              onPaymentIdChange={(value) => {
                // console.log('>> onPaymentIdChange')
                onRowChange(index, undefined, value, row.expireDate)
              }}
              onDeleteRow={onDeleteRow}
              startOpen={row.startOpen || false}
              error={getRowError(index)}
              readonly={isPaymentWithGateway(row.paymentActiveId) || readonly || isSplitPayment(row.paymentActiveId)}
              disabled={isPaymentWithGateway(row.paymentActiveId) || disabled || isSplitPayment(row.paymentActiveId)}
              disablePriceEdit={depositCreation || disablePriceEdit}
              disableRowsChange={disablePriceEdit}
              showExpirationDate={row.paymentActiveId ? isUnpaidPaymentMethod(row.paymentActiveId) : undefined}
              onExpirationDateChange={(value) => {
                // console.log('>> onExpirationDateChange')
                onRowChange(index, row.amount, row.paymentActiveId, value)
              }}
            />
          </React.Fragment>
        )
      })}
    </Grid>
  )
}

function toPaymentAmount(p: PaymentRowAmount & { startOpen?: boolean; editable: boolean }): PaymentRowAmount {
  return {
    uuid: p.uuid,
    paymentActiveId: p.paymentActiveId,
    amount: p.amount,
    editable: p.editable,
    expireDate: p.expireDate,
  }
}

function toPaymentAmounts(ps: (PaymentRowAmount & { startOpen?: boolean })[]): PaymentRowAmount[] {
  return ps.reduce((p: PaymentRowAmount[], c) => [...p, toPaymentAmount(c)], [])
}
